import EntityTable from "@mirage/components/entityTable/EntityTable";
import LinkCell from "@mirage/components/entityTable/components/LinkCell";
import { useDeviceDetect } from "@mirage/hooks/useDeviceDetect";
import useSessionStorage from "@mirage/hooks/useSessionStorage";
import { TablePaginationBaseProps } from "@mui/material/TablePagination/TablePagination";
import { Box } from "@mui/system";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid-premium";
import PlanningApplicationsService from "@publicPortal/services/PlanningApplicationsService";
import { PublicPortalRoutingService } from "@publicPortal/services/PublicPortalRoutingService";
import { IEntityPageRequest } from "@shared/mirage/core/contracts/IEntityPageRequest";
import { IEntityPageResult } from "@shared/mirage/core/contracts/IEntityPageResult";
import { Filter } from "@shared/mirage/entityTable/Filter";
import { PApplicationBinding } from "@shared/planningmanagement/metadata/PApplicationBinding";
import { PPConstants } from "@shared/publicportal/constants";
import React, { memo, useCallback, useMemo } from "react";

export interface SearchResultTableProps {
    filter: Filter;
    title?: string;
    customPageRequest?: (pageRequest: IEntityPageRequest) => Promise<IEntityPageResult<unknown>>;
}

const planningAppService = new PlanningApplicationsService();
const pp = new PublicPortalRoutingService();

const PlanningApplicationsTable: React.FC<SearchResultTableProps> = memo(({ filter, title }) => {
    const { isMobile } = useDeviceDetect();
    const [sessionValue] = useSessionStorage(PPConstants.AppName);
    const visibleColumns = sessionValue.clientSettings.PLANNING_APPLICATION_RESULT_TABLE_COLUMNS;

    const labelsMap = PApplicationBinding.metadata().labelsMap;

    const nameColumn: GridColDef = useMemo(
        () => ({
            field: "name",
            headerName: "Application Reference",
            renderCell: (params: GridRenderCellParams) => <LinkCell value={params.value} route={pp.PlanningApplicationRecordRoute(Number(params.id))} />,
            width: 140,
        }),
        []
    );

    const columns = useMemo(() => {
        if (!Array.isArray(visibleColumns) || visibleColumns.length === 0) {
            return [nameColumn];
        }

        const dynamicColumns: GridColDef[] = visibleColumns.map((fieldName) => {
            let headerName = labelsMap.get(fieldName) || fieldName;

            if (fieldName == "status") {
                headerName = "Status";
            } else if (fieldName == "address") {
                headerName = "Address";
            }

            let width = undefined;
            let flex = undefined;
            let type: "date" | undefined = undefined;

            if (fieldName.includes("Date")) {
                type = "date";
                width = 120;
            } else if (fieldName === "address") {
                width = isMobile ? 200 : 0;
            } else if (fieldName === "proposal") {
                width = isMobile ? 200 : 0;
            } else if (fieldName === "status" || fieldName === "decision") {
                width = 100;
            } else {
                flex = 1;
            }

            return {
                field: fieldName,
                headerName,
                width,
                flex,
                type,
                ...(fieldName === "address" ? { flex: 0.75 } : {}),
                ...(fieldName === "proposal" ? { flex: 1 } : {}),
            } as GridColDef;
        });

        return [nameColumn, ...dynamicColumns];
    }, [nameColumn, visibleColumns, labelsMap, isMobile]);
    const onPageRequest = useCallback(async (pageRequest: IEntityPageRequest) => await planningAppService.pageRequest(pageRequest), []);
    const overridePaginationProps = useCallback((baseProps: TablePaginationBaseProps) => ({ ...baseProps, showFirstButton: false, showLastButton: false }), []);
    const getRowHeight = useCallback(() => "auto", []);

    return (
        <Box sx={{ height: "auto" }}>
            <EntityTable
                title={title}
                key="planning-applications-entityTable"
                stateKey="PlanningApplications"
                onPageRequest={onPageRequest}
                columns={columns}
                filter={filter}
                getRowHeight={getRowHeight}
                hideRowsPerPage={isMobile}
                overridePaginationProps={overridePaginationProps}
                entityName={PApplicationBinding.metadata().entityName}
            />
        </Box>
    );
});

export default PlanningApplicationsTable;
